import { ScoreStatusEnum } from 'lms/types/instructor';
import { CertificateMetadataType } from './dashboard';

export type SearchPayload = {
  search: string;
  contentTypes: ContentType[];
  statusTypes: StatusType[];
  enrollType: EnrollType[];
  startDate: {
    from: string;
    to: string;
  } | null;
  orderBy: OrderBy;
  viewType: ViewType;
  page: number;
  limit: number;
};

export type SearchPaylodWithoutPagination = Omit<
  SearchPayload,
  'page' | 'limit'
>;

export enum ContentType {
  ALL = 'ALL',
  COURSE = 'COURSE',
  LP = 'LP',
  LPC = 'LPC',
  ONLINE = 'ONLINE',
  SCHEDULED = 'SCHEDULED',
  FAVOURITES = 'FAVOURITES',
}

export enum StatusType {
  ALL = 'ALL',
  INACTIVE = 'INACTIVE',
  APPROVED = 'APPROVED',
  IN_PROGRESS = 'IN_PROGRESS',
  FAILED = 'FAILED',
}

export enum EnrollType {
  ALL = 'ALL',
  AUTO_ENROLL = 'AUTO_ENROLL',
  ADMIN_ENROLL = 'ADMIN_ENROLL',
}

export enum StartDate {
  NO_DATE = 'NO_DATE',
  TODAY = 'TODAY',
  THIS_WEEK = 'THIS_WEEK',
  NEXT_WEEK = 'NEXT_WEEK',
  THIS_MONTH = 'THIS_MONTH',
  CUSTOM = 'CUSTOM',
}

export enum OrderBy {
  RECENT = 'RECENT',
  PROGRESS = 'PROGRESS',
  NAME = 'NAME',
  ENROLL_DATE = 'ENROLL_DATE',
  EXPIRATION_DATE = 'EXPIRATION_DATE',
}

export enum ViewType {
  CARDS = 'CARDS',
  TABLE = 'TABLE',
}

// ------------------------------

export type BaseSearchContent = {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  clientId: string;
  status: StatusType;
  progress: number;
  completedAt: string;
  startDate: string;
  endDate: string;
  gamificationEnabled: boolean;
  gamificationPoints: number;
  canUnsubscribe: boolean;
  canDownloadCertificate: boolean;
  contentType: ContentType;
  favorited: boolean;
  hasReaccreditation: boolean;
  reaccreditationStatus: ReaccreditationStatus;
  hasQualtrics: boolean;
};

export type CourseSearchResponse = {
  courses: CourseSearch[];
  total: number;
};

export type RouteSearchResponse = {
  routes: RouteSearch[];
  total: number;
};

export type AllResourcesSearchResponse = {
  allResources: AllResourcesSearch[];
  total: number;
};

export type CourseSearch = BaseSearchContent & {
  type: CourseTypes;
  ownerSubdomain: string;
  catalogId: string;
  ownerName: string;
  ownerImageUrl: string;
  score: number;
  isEnabledDc3: boolean;
  isEnabledDc3InCourse: boolean;
  dateToShowDC3: string | null;
  canEnrollLeader?: boolean;
  qualificationStatus?: ScoreStatusEnum;
};

export type RouteSearch = BaseSearchContent & {
  lpcId?: string;
  resources: Array<{
    id: string;
    name: string;
    can_enroll?: boolean;
  }>;
  matchCourses: number;
};

export type AllResourcesSearch = {
  id: string;
  lpcId?: string;
  name: string;
  imageUrl: string; // add this
  type: CourseTypes;
  startDate: string;
  endDate: string;
  progress: number;
  status: StatusType;
  canUnsubscribe: boolean;
  canDownloadCertificate: boolean;
  contentType: ContentType;
  completedAt: string;
  favorited: boolean;
  gamificationEnabled: boolean;
  gamificationPoints: number;
  hasReaccreditation: boolean;
  reaccreditationStatus: ReaccreditationStatus;
  hasQualtrics: boolean;
  score: number;
  isEnabledDc3: boolean;
  isEnabledDc3InCourse: boolean;
  dateToShowDC3: string | null;
  canEnrollLeader?: boolean;
  qualificationStatus?: ScoreStatusEnum;
};

export enum CourseTypes {
  ONLINE = 'ONLINE',
  SCHEDULED = 'SCHEDULED',
  EXTERNAL = 'EXTERNAL',
}

export enum ReaccreditationStatus {
  INACTIVE = 'INACTIVE',
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
}

export type HandleDownloadCertificate = <
  T extends CourseSearch | RouteSearch | AllResourcesSearch,
>({
  type,
  resource,
  subtype,
}: {
  type: CertificateMetadataType;
  resource: T;
  subtype: 'card' | 'table';
}) => Promise<T>;

export type HandleFavorite = ({
  type,
  resourceId,
  isFavorite,
  from,
}: {
  type: CertificateMetadataType;
  resourceId: string;
  isFavorite: boolean;
  from: 'table' | 'cards';
}) => Promise<void>;

export type HandleUnsubscribe = ({
  resourceId,
  contentType,
  from,
}: {
  resourceId: string;
  resourceName: string;
  contentType: 'LP' | 'COURSE';
  from: 'table' | 'cards';
}) => Promise<void>;

export type UnsubscribeData = {
  resourceId: string;
  resourceName: string;
  contentType: 'LP' | 'COURSE';
  from: 'table' | 'cards';
};

export type HandleEnrollMyTeam = ({
  resourceId,
  resourceType,
}: {
  resourceId: string;
  resourceType: ContentType;
}) => void;
